/**
 * The deterministic DRAW PLAN for the hand-drawn map (Spatial Map System R8–R11).
 * A pure function from a sanitized map payload (+ its per-node fixed render seeds)
 * to an ordered list of primitive draw commands. The visual layer (Rough.js +
 * Konva, wired separately) consumes this plan to paint the parchment map; THIS
 * module is the reproducible core and the thing the determinism test pins.
 *
 * Determinism (R11.2): every wobble/jitter in a node's hull or an edge's curve is
 * drawn from a {@link SeededRandom} seeded with that node's `renderSeed` — so the
 * same payload always yields a byte-identical plan. No `Math.random` anywhere
 * (R11.6; guarded by `no-math-random.test.ts`).
 *
 * BOUNDARY (R8.4/R11): shapes/dims drive NARRATIVE geometry + the drawing only —
 * never a combat grid, blast radius, or per-tile occupancy. The plan has no
 * notion of distance-for-attack.
 */

import { SeededRandom } from "./seeded-random.js";

/** The map tier a node lives at (Organic Map Layout R4) — selects the backdrop + scale style. */
export type RenderHierarchyLevel = "micro" | "meso" | "macro";

/** The sanitized, player-safe node the renderer draws (wire shape from the read API). */
export interface RenderNode {
  readonly nodeId: string;
  readonly coord: { readonly x: number; readonly y: number; readonly z: number };
  readonly shapeProfile:
    | "square"
    | "rectangular"
    | "circular"
    | "octagonal"
    | "irregular_cavern"
    | "meandering_tunnel"
    | "chasm_ledge";
  readonly dims?: { readonly widthFt: number; readonly lengthFt: number; readonly ceilingFt?: number };
  readonly renderSeed: number;
  readonly illumination: "bright" | "dim" | "darkness";
  /** Active-vision flag (R12.3): a discovered-but-unseen node draws under the darkness mask. */
  readonly visible: boolean;
  /**
   * The node's map tier (Organic Map Layout R4.1/R4.2). OPTIONAL + defaulting to
   * `"micro"` so existing payloads/fixtures that omit it keep working. It is a
   * non-secret structural field the sanitized payload already carries
   * (`SanitizedNode.hierarchyLevel`); the renderer reads it to pick the per-tier
   * backdrop and the grid-vs-legend scale treatment — NEVER the DM-only `placeKind`.
   */
  readonly hierarchyLevel?: RenderHierarchyLevel;
}

/** The physical FORM of a site's way out (mirrors the shared-types `ExitKind`). */
export type RenderExitKind =
  | "opening"
  | "stair_up"
  | "trapdoor"
  | "door_at_grade"
  | "cave_mouth"
  | "ramp"
  | "portal";

/** A compass/vertical direction on an edge (mirrors the shared-types `Direction`). */
export type RenderDirection =
  | "north" | "south" | "east" | "west"
  | "northeast" | "northwest" | "southeast" | "southwest"
  | "up" | "down";

/** The sanitized edge the renderer draws (hidden edges never reach the client). */
export interface RenderEdge {
  readonly edgeId: string;
  readonly from: string;
  readonly to: string;
  /**
   * The compass direction of the edge FROM `from` TO `to` (R9). This is what
   * lets the renderer place the doorway on the CORRECT WALL — a corridor leaving
   * a room to the east exits the east wall — so "two doors east, one west" reads
   * true instead of three lines meeting at the room center.
   */
  readonly direction?: RenderDirection;
  readonly bearingDeflection?:
    | "straight"
    | "gentle_curve_left"
    | "gentle_curve_right"
    | "sharp_left"
    | "sharp_right"
    | "meandering";
  readonly barrier?: { readonly barrierType: string; readonly status: string };
  readonly destroyed?: boolean;
  /**
   * A STUB edge (R4/R11.6): a known opening out of a DISCOVERED room toward a
   * node the party has NOT discovered. `to` is empty and no target geometry is
   * carried — the renderer draws a short passage running off into the rock so
   * the corridor visibly "continues", without revealing where it goes.
   */
  readonly leadsToUndiscovered?: boolean;
  /**
   * When set, this edge is the site's WAY OUT to the world (surface-egress addendum); the
   * value is the egress FORM. The renderer draws a dedicated EXIT affordance per kind
   * (stairs-up/trapdoor badge, a door, a cave-mouth opening, a ramp, a portal ring) rather
   * than a plain "corridor continues" stub — so the map matches the DM's narrated way out.
   */
  readonly egress?: RenderExitKind;
}

/** The sanitized map payload (one Z-slice) the renderer paints. */
export interface RenderPayload {
  readonly nodes: readonly RenderNode[];
  readonly edges: readonly RenderEdge[];
}

/** A single primitive draw command. The visual layer maps each to a Rough.js/Konva call. */
export type DrawCommand =
  | {
      readonly kind: "polygon";
      readonly nodeId: string;
      /** Absolute points (closed ring) with the node's seeded sketch jitter applied. */
      readonly points: readonly (readonly [number, number])[];
      readonly masked: boolean;
    }
  | {
      readonly kind: "hull";
      readonly nodeId: string;
      /** A seeded organic hull (caverns/ledges) — points wobble around the footprint. */
      readonly points: readonly (readonly [number, number])[];
      readonly masked: boolean;
    }
  | {
      readonly kind: "edge";
      readonly edgeId: string;
      /** The polyline/Bezier control points between the two nodes' centers. */
      readonly points: readonly (readonly [number, number])[];
      /** True for a curved/meandering corridor (drawn as a Bezier), false for a straight line. */
      readonly curved: boolean;
      readonly destroyed: boolean;
    }
  | {
      /**
       * A corridor's FLOOR CHANNEL — the walkable strip of floor the passage
       * carves through the surrounding rock (R8/R11). The renderer paints this
       * (plus the room floors) into the "floor" mask so the parchment shows
       * through, which is what makes a doorway read as a GAP in the wall mass:
       * the channel punches the opening between two rooms' floors rather than a
       * thin line meeting an unbroken wall. A quad (two parallel offsets of the
       * doorway-to-doorway centerline) for a straight corridor; curved corridors
       * carry their Bezier spine in `spine` so the renderer can sweep a ribbon.
       */
      readonly kind: "channel";
      readonly edgeId: string;
      /** The channel's closed quad ring (absolute points), half-open at each end into its room. */
      readonly points: readonly (readonly [number, number])[];
      /** The doorway-to-doorway centerline (2 pts straight, 3 pts for a curved spine). */
      readonly spine: readonly (readonly [number, number])[];
      /** The channel half-width in feet (the corridor is `2*halfWidth` wide). */
      readonly halfWidth: number;
      readonly curved: boolean;
      readonly destroyed: boolean;
      /**
       * True for a STUB channel — a short passage running from a discovered
       * room's doorway OUT into the rock toward an undiscovered place (its far
       * end is open/unexplored, not a second room). The renderer fades the far
       * end so it reads "the corridor continues, unseen" (R4/R11.6).
       */
      readonly stub?: boolean;
      /**
       * A VERTICAL connection (stairs/ladder/shaft) going `"up"` or `"down"` a
       * Z-level. A vertical link's far end lives on ANOTHER floor, so on this
       * 2D Z-slice it is drawn as a SELF-CONTAINED stair STAMP docked to the
       * room wall — a short landing channel tagged with this direction so the
       * renderer paints stair treads + an "▲ UP" / "▼ DN" badge instead of a
       * diagonal corridor (which would misread as a northeast passage). Absent
       * for an ordinary horizontal corridor.
       */
      readonly vertical?: "up" | "down";
      /**
       * An EGRESS channel — the site's WAY OUT to the world (surface-egress addendum). Like a
       * stub, it runs from the boundary room's doorway OUT with no second room; its far end is
       * the edge of the site. The value is the egress FORM, so the visual layer draws a
       * dedicated EXIT affordance (stairs-up/trapdoor + "EXIT" badge, a door, a cave-mouth
       * opening, a ramp, a portal ring) rather than a plain "corridor continues" stub.
       */
      readonly egress?: RenderExitKind;
    }
  | {
      readonly kind: "barrier";
      readonly edgeId: string;
      /** The midpoint of the edge where the door/gate glyph is stamped. */
      readonly at: readonly [number, number];
      readonly barrierType: string;
      readonly status: string;
    }
  | {
      /**
       * MICRO-only scale GRID LINES (Organic Map Layout R5.1/R5.2) — a visual size
       * ruler at a 5-ft (or viewport-adapted multiple-of-5) interval, aligned to
       * the unified coordinate origin, drawn BENEATH the backdrop + nodes. A pure
       * reference ruler: it never repositions a node and is never consumed by any
       * engine/combat path (R5.6). Lines are in MAP (Y-up) coords.
       */
      readonly kind: "gridlines";
      readonly intervalFt: number;
      readonly lines: readonly { readonly from: readonly [number, number]; readonly to: readonly [number, number] }[];
    }
  | {
      /**
       * MESO/MACRO scale LEGEND / bar (Organic Map Layout R5.3) — a labeled bar
       * (no grid lines at these tiers) reading a per-tier real-world distance
       * (hundreds of feet at meso, miles at macro). `lengthFt` is its length in map
       * feet; `label` is the human text; `at` is its anchor (map coords, lower-left).
       */
      readonly kind: "scalebar";
      readonly lengthFt: number;
      readonly label: string;
      readonly at: readonly [number, number];
    }
  | {
      /**
       * A per-tier descriptive BACKDROP fill (Organic Map Layout R4) under the
       * nodes/edges — a seeded organic region keyed on `tier` + the node's shape.
       * Render-only; drawn ABOVE the grid, BELOW the nodes (z-order
       * grid < backdrop < nodes < edges). Seeded independently of the node ring
       * (a salted PRNG) so it never shifts the node's own jitter.
       */
      readonly kind: "backdrop";
      readonly ofNodeId: string;
      readonly tier: RenderHierarchyLevel;
      readonly points: readonly (readonly [number, number])[];
      readonly masked: boolean;
    };

/** Shapes drawn as STRICT geometry (constructed spaces) vs. organic seeded hulls. */
const CONSTRUCTED = new Set(["square", "rectangular", "circular", "octagonal"]);

/** A salt so a backdrop's seeded PRNG stream never collides with the node ring's. */
const BACKDROP_SALT = 0x9e3779b9;
/** The base micro grid interval in feet (a 5-ft square ruler). */
const MICRO_GRID_FT = 5;
/**
 * The maximum number of grid cells to draw across the content's larger dimension
 * before the 5-ft interval is stepped up to the next multiple of 5. This keeps a
 * LARGE micro area (a sprawling cavern hundreds of feet across) from rendering an
 * unreadably dense 5-ft grid, while a normal room keeps true 5-ft squares. A
 * higher `viewportScale` (zoomed in) tolerates MORE cells before stepping up.
 */
const MAX_GRID_CELLS = 48;
/** The default tier when a node omits `hierarchyLevel` (back-compat). */
const DEFAULT_TIER: RenderHierarchyLevel = "micro";

/** A node's tier, defaulting to micro for back-compat with pre-tier payloads. */
function tierOf(node: RenderNode): RenderHierarchyLevel {
  return node.hierarchyLevel ?? DEFAULT_TIER;
}

/** The payload's dominant tier (the tier of the most nodes) — picks grid vs legend. */
function dominantTier(payload: RenderPayload): RenderHierarchyLevel {
  const counts: Record<RenderHierarchyLevel, number> = { micro: 0, meso: 0, macro: 0 };
  for (const n of payload.nodes) counts[tierOf(n)]++;
  if (counts.macro >= counts.meso && counts.macro >= counts.micro && counts.macro > 0) return "macro";
  if (counts.meso >= counts.micro && counts.meso > 0) return "meso";
  return "micro";
}

/** The content extent (map coords) over all node footprints, for the grid/scale span. */
function nodeExtent(payload: RenderPayload): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of payload.nodes) {
    const hw = n.dims ? n.dims.widthFt / 2 : 5;
    const hl = n.dims ? n.dims.lengthFt / 2 : 5;
    minX = Math.min(minX, n.coord.x - hw);
    maxX = Math.max(maxX, n.coord.x + hw);
    minY = Math.min(minY, n.coord.y - hl);
    maxY = Math.max(maxY, n.coord.y + hl);
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/**
 * The micro grid interval (feet): the smallest multiple of 5 that draws at most
 * `MAX_GRID_CELLS` cells across the content's larger dimension — so a normal room
 * keeps true 5-ft squares and only a sprawling micro area steps the interval up.
 * `viewportScale` (the zoom transform, ≥1 = zoomed in) RAISES the tolerance, so a
 * zoomed-in large area can still show finer squares. Deterministic in both inputs
 * (R5.4/R5.5): the same (extent, zoom) always yields the same interval.
 */
function microGridInterval(payload: RenderPayload, viewportScale: number): number {
  const { minX, minY, maxX, maxY } = nodeExtent(payload);
  const spanFt = Math.max(1, Math.max(maxX - minX, maxY - minY));
  const budget = MAX_GRID_CELLS * Math.max(1, viewportScale);
  let interval = MICRO_GRID_FT;
  let guard = 0;
  while (spanFt / interval > budget && guard < 32) {
    interval += MICRO_GRID_FT;
    guard++;
  }
  return interval;
}

/** Build the micro 5-ft grid-line segments across the content extent, origin-aligned. */
function microGridLines(payload: RenderPayload, intervalFt: number): DrawCommand {
  const { minX, minY, maxX, maxY } = nodeExtent(payload);
  // Pad a little so lines frame the content.
  const pad = intervalFt * 2;
  const x0 = Math.floor((minX - pad) / intervalFt) * intervalFt;
  const x1 = Math.ceil((maxX + pad) / intervalFt) * intervalFt;
  const y0 = Math.floor((minY - pad) / intervalFt) * intervalFt;
  const y1 = Math.ceil((maxY + pad) / intervalFt) * intervalFt;
  const lines: { from: readonly [number, number]; to: readonly [number, number] }[] = [];
  for (let x = x0; x <= x1; x += intervalFt) lines.push({ from: [x, y0], to: [x, y1] });
  for (let y = y0; y <= y1; y += intervalFt) lines.push({ from: [x0, y], to: [x1, y] });
  return { kind: "gridlines", intervalFt, lines };
}

/**
 * A "nice" scale-bar length (feet) + human label for meso/macro, chosen as the
 * largest 1/2/5×10^k feet that is ≤ ~a quarter of the content width (so the bar
 * reads without dominating). Meso labels in feet; macro converts to miles.
 */
function scaleBar(payload: RenderPayload, tier: RenderHierarchyLevel): DrawCommand {
  const { minX, minY, maxX } = nodeExtent(payload);
  const widthFt = Math.max(1, maxX - minX);
  const target = widthFt / 4;
  // The largest 1/2/5 x 10^k not exceeding target.
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(target, 1))));
  const candidates = [5 * pow, 2 * pow, 1 * pow, 0.5 * pow];
  const lengthFt = candidates.find((c) => c <= target) ?? pow;
  const label =
    tier === "macro"
      ? `${formatMiles(lengthFt)} mi`
      : `${Math.round(lengthFt)} ft`;
  // Anchor the bar at the lower-left of the content (map coords).
  return { kind: "scalebar", lengthFt, label, at: [minX, minY] };
}

/** Feet→miles for the macro label (5280 ft/mi), trimmed to a readable number. */
function formatMiles(ft: number): string {
  const miles = ft / 5280;
  return miles >= 10 ? String(Math.round(miles)) : miles.toFixed(1).replace(/\.0$/, "");
}

/**
 * The per-tier descriptive BACKDROP fills (Organic Map Layout R4.2), keyed on the
 * node's tier + shapeProfile ONLY (never the DM-only placeKind). Seeded from a
 * SALTED renderSeed so the backdrop never consumes the node ring's PRNG stream.
 * Micro: a slightly-larger wall-mass hull/box around the room. Meso: a blockier
 * building footprint. Macro: a broad organic terrain hull. All in map (Y-up) coords.
 */
function backdropCommands(payload: RenderPayload): DrawCommand[] {
  const out: DrawCommand[] = [];
  const nodes = [...payload.nodes].sort((a, b) => (a.nodeId < b.nodeId ? -1 : a.nodeId > b.nodeId ? 1 : 0));
  for (const node of nodes) {
    const tier = tierOf(node);
    const rng = new SeededRandom((node.renderSeed ^ BACKDROP_SALT) >>> 0);
    const { halfW, halfL } = halfExtents(node);
    // The backdrop is a touch larger than the node footprint so it reads as the
    // ground/mass the room/building/settlement sits in.
    const scale = tier === "macro" ? 1.6 : tier === "meso" ? 1.25 : 1.15;
    const n = tier === "micro" && CONSTRUCTED.has(node.shapeProfile) ? 4 : 12;
    const pts: [number, number][] = [];
    for (let i = 0; i < n; i++) {
      const a = (2 * Math.PI * i) / n + (n === 4 ? Math.PI / 4 : 0);
      // Macro/organic backdrops wobble more; a constructed micro backdrop is blockier.
      const wobble = tier === "micro" && CONSTRUCTED.has(node.shapeProfile) ? 1 : 0.75 + rng.next() * 0.5;
      pts.push([
        node.coord.x + Math.cos(a) * halfW * scale * wobble,
        node.coord.y + Math.sin(a) * halfL * scale * wobble,
      ]);
    }
    out.push({
      kind: "backdrop",
      ofNodeId: node.nodeId,
      tier,
      points: pts,
      masked: !node.visible || node.illumination === "darkness",
    });
  }
  return out;
}

/** The jitter magnitude (in feet) for the hand-drawn sketch wobble on a vertex. */
const SKETCH_JITTER = 0.6;

/**
 * Half-width (feet) of a carved corridor channel. A corridor is drawn as a
 * ~`2*CHANNEL_HALF_WIDTH`-ft-wide strip of floor punched through the rock, wide
 * enough to read as a passage/opening (not a hairline) at map zoom.
 */
const CHANNEL_HALF_WIDTH = 4;

/**
 * How far (feet) a STUB passage runs out from a room's doorway into the rock
 * before its open, unexplored end. Long enough to read as "a corridor leaves
 * here and continues", short enough that it clearly stops at the fog boundary.
 */
const STUB_LENGTH_FT = 16;

/**
 * How far (feet) a VERTICAL stair landing extends off a room's wall before the
 * stair stamp. Shorter than a horizontal stub — a stairwell is a compact landing
 * docked to the wall, not a corridor running off across the map.
 */
const VERTICAL_STAIR_LENGTH_FT = 10;

/**
 * The closed quad for a STRAIGHT corridor channel: the doorway-to-doorway
 * segment offset perpendicularly by ±`halfWidth`, with each end pushed a little
 * INTO its room (`overlapFt`) so the channel floor overlaps the room floor and
 * the opening carves cleanly through the wall rather than leaving a seam at the
 * doorway. Returns the 4 corners (ring order).
 */
function channelQuad(
  from: readonly [number, number],
  to: readonly [number, number],
  halfWidth: number,
  overlapFt: number,
): (readonly [number, number])[] {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len; // unit along the corridor
  const uy = dy / len;
  const px = -uy; // unit perpendicular
  const py = ux;
  // Extend each end into its room so the floors merge at the mouth.
  const a: [number, number] = [from[0] - ux * overlapFt, from[1] - uy * overlapFt];
  const b: [number, number] = [to[0] + ux * overlapFt, to[1] + uy * overlapFt];
  return [
    [a[0] + px * halfWidth, a[1] + py * halfWidth],
    [b[0] + px * halfWidth, b[1] + py * halfWidth],
    [b[0] - px * halfWidth, b[1] - py * halfWidth],
    [a[0] - px * halfWidth, a[1] - py * halfWidth],
  ];
}

/** Half-extents of a node's footprint (a dim-less node is a small fixed marker). */
function halfExtents(node: RenderNode): { halfW: number; halfL: number } {
  if (!node.dims) return { halfW: 5, halfL: 5 };
  return { halfW: node.dims.widthFt / 2, halfL: node.dims.lengthFt / 2 };
}

/**
 * Unit direction vectors in MAP coordinates (engine convention: NORTH = +y, EAST
 * = +x; the engine's `directionBetween` reads dy>0 as north). The MapCanvas flips
 * Y into SVG space, so the draw-plan works entirely in map coords. `up`/`down`
 * are vertical (cross-Z) connections with no in-plane direction — they get a tiny
 * NE nudge so a doorway still lands on a wall rather than the center. Diagonals
 * are normalized to unit length so the wall-intersection ray is isotropic.
 */
const DIR_VEC: Record<RenderDirection, readonly [number, number]> = {
  north: [0, 1],
  south: [0, -1],
  east: [1, 0],
  west: [-1, 0],
  northeast: [Math.SQRT1_2, Math.SQRT1_2],
  northwest: [-Math.SQRT1_2, Math.SQRT1_2],
  southeast: [Math.SQRT1_2, -Math.SQRT1_2],
  southwest: [-Math.SQRT1_2, -Math.SQRT1_2],
  up: [Math.SQRT1_2, Math.SQRT1_2], // vertical: nudge to a corner so it reads as a stair-opening
  down: [-Math.SQRT1_2, -Math.SQRT1_2],
};

/** The opposite compass direction — the wall an edge ENTERS the `to` node by. */
const INVERSE_DIR: Record<RenderDirection, RenderDirection> = {
  north: "south", south: "north", east: "west", west: "east",
  northeast: "southwest", southwest: "northeast",
  northwest: "southeast", southeast: "northwest",
  up: "down", down: "up",
};

/**
 * The point on a node's perimeter `ring` where a ray from `center` in compass
 * `direction` exits — i.e. the DOORWAY on that wall (R8/R9). Casts the ray
 * against each polygon edge of the ring and returns the nearest forward
 * intersection. This is what puts a door on the east wall for an east-leaving
 * corridor instead of at the room center. Falls back to `center` if the ray
 * somehow misses (degenerate ring), so a draw never produces NaN.
 */
export function perimeterPointInDirection(
  ring: readonly (readonly [number, number])[],
  center: readonly [number, number],
  direction: RenderDirection,
): readonly [number, number] {
  const [dx, dy] = DIR_VEC[direction];
  const [cx, cy] = center;
  let best: readonly [number, number] | undefined;
  let bestT = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const p1 = ring[i]!;
    const p2 = ring[(i + 1) % ring.length]!;
    // Solve center + t*dir = p1 + u*(p2-p1), for t >= 0 and u in [0,1].
    const ex = p2[0] - p1[0];
    const ey = p2[1] - p1[1];
    const denom = dx * ey - dy * ex;
    if (Math.abs(denom) < 1e-9) continue; // ray parallel to this edge
    const wx = p1[0] - cx;
    const wy = p1[1] - cy;
    const t = (wx * ey - wy * ex) / denom; // distance along the ray
    const u = (wx * dy - wy * dx) / denom; // position along the polygon edge
    if (t >= 0 && u >= -1e-9 && u <= 1 + 1e-9 && t < bestT) {
      bestT = t;
      best = [cx + dx * t, cy + dy * t];
    }
  }
  return best ?? center;
}

/**
 * An AXIS-ALIGNED rectangle's four corners (pre-jitter), centered on the node's
 * coord. A `square`/`rectangular` room must read as a box with horizontal and
 * vertical walls — its corners sit at the DIAGONALS (±halfW, ±halfL). (The prior
 * code built a 4-gon from `cos/sin` starting at −90°, which placed vertices at
 * the EDGE MIDPOINTS and drew a 45°-rotated DIAMOND instead of a room — the bug
 * behind the "two diamonds" map.)
 */
function rectangleCorners(node: RenderNode): (readonly [number, number])[] {
  const { halfW, halfL } = halfExtents(node);
  const { x, y } = node.coord;
  return [
    [x - halfW, y - halfL],
    [x + halfW, y - halfL],
    [x + halfW, y + halfL],
    [x - halfW, y + halfL],
  ];
}

/**
 * A regular N-gon's base vertices (pre-jitter), centered on the node's coord.
 * `rotationRad` orients the polygon: an octagon uses a half-step offset so it
 * sits flat-topped (a room), and a circle uses many sides so orientation is
 * irrelevant.
 */
function polygonVertices(node: RenderNode, sides: number, rotationRad = 0): (readonly [number, number])[] {
  const { halfW, halfL } = halfExtents(node);
  const pts: [number, number][] = [];
  for (let i = 0; i < sides; i++) {
    const a = rotationRad + (2 * Math.PI * i) / sides;
    pts.push([node.coord.x + Math.cos(a) * halfW, node.coord.y + Math.sin(a) * halfL]);
  }
  return pts;
}

/** Apply the node's seeded sketch jitter to each vertex (the hand-drawn wobble). */
function sketch(points: readonly (readonly [number, number])[], rng: SeededRandom): (readonly [number, number])[] {
  return points.map(([x, y]) => [x + rng.jitter(SKETCH_JITTER), y + rng.jitter(SKETCH_JITTER)] as const);
}

/** The base vertex ring for a shape profile (constructed = exact N-gon; organic = jagged hull). */
function baseRing(node: RenderNode, rng: SeededRandom): (readonly [number, number])[] {
  switch (node.shapeProfile) {
    case "square":
    case "rectangular":
      // Axis-aligned box (corners at the diagonals), so it reads as a room with
      // flat horizontal/vertical walls rather than a rotated diamond.
      return rectangleCorners(node);
    case "octagonal":
      // Half-step rotation (π/8) gives the octagon a flat top/bottom + flat
      // sides (a room), not a point-up gem.
      return polygonVertices(node, 8, Math.PI / 8);
    case "circular":
      return polygonVertices(node, 24); // many sides ≈ a drawn circle
    case "irregular_cavern":
    case "chasm_ledge":
    case "meandering_tunnel": {
      // Organic: a 12-point hull whose radius wobbles per vertex (seeded).
      const { halfW, halfL } = halfExtents(node);
      const pts: [number, number][] = [];
      const n = 12;
      for (let i = 0; i < n; i++) {
        const a = (2 * Math.PI * i) / n;
        const r = 0.7 + rng.next() * 0.5; // 0.7–1.2 of the footprint radius
        pts.push([node.coord.x + Math.cos(a) * halfW * r, node.coord.y + Math.sin(a) * halfL * r]);
      }
      return pts;
    }
  }
}

/** Is a corridor curved (drawn as a Bezier) rather than straight? */
function isCurved(edge: RenderEdge): boolean {
  const d = edge.bearingDeflection;
  return d !== undefined && d !== "straight";
}

/**
 * Build the deterministic draw plan for a sanitized map payload (R8–R11). Nodes
 * are drawn first (constructed = strict polygon, organic = seeded hull), then the
 * edges connecting their centers (curved corridors as Beziers), then the barrier
 * glyphs. The command ORDER and every jittered coordinate are fully determined by
 * the payload + the per-node `renderSeed`, so two calls with the same payload are
 * byte-identical. Pure — no clock, no `Math.random`.
 */
export function buildDrawPlan(payload: RenderPayload, viewportScale = 1): readonly DrawCommand[] {
  const commands: DrawCommand[] = [];

  // LAYER 0 — SCALE LEGIBILITY + BACKDROP, emitted FIRST so they paint beneath
  // the nodes/edges (z-order grid/scalebar < backdrop < nodes < edges). Micro gets
  // 5-ft grid lines (a size ruler); meso/macro get a scale legend, no grid (R5).
  if (payload.nodes.length > 0) {
    const tier = dominantTier(payload);
    if (tier === "micro") {
      commands.push(microGridLines(payload, microGridInterval(payload, viewportScale)));
    } else {
      commands.push(scaleBar(payload, tier));
    }
    // The per-tier descriptive backdrop, above the grid, below the nodes (R4).
    for (const b of backdropCommands(payload)) commands.push(b);
  }

  const centerOf = new Map<string, readonly [number, number]>();
  // The sketched perimeter ring of each node, kept so an edge can find the point
  // on a wall in its compass direction (the doorway) rather than meeting at the
  // room center. Deterministic (seeded), so the stored ring matches what was drawn.
  const ringOf = new Map<string, readonly (readonly [number, number])[]>();

  // Nodes in a stable order (sorted by nodeId) so the plan does not depend on the
  // payload array order — only on its content + seeds.
  const nodes = [...payload.nodes].sort((a, b) => (a.nodeId < b.nodeId ? -1 : a.nodeId > b.nodeId ? 1 : 0));
  for (const node of nodes) {
    centerOf.set(node.nodeId, [node.coord.x, node.coord.y]);
    const rng = new SeededRandom(node.renderSeed);
    const ring = sketch(baseRing(node, rng), rng);
    ringOf.set(node.nodeId, ring);
    const masked = !node.visible || node.illumination === "darkness";
    if (CONSTRUCTED.has(node.shapeProfile)) {
      commands.push({ kind: "polygon", nodeId: node.nodeId, points: ring, masked });
    } else {
      commands.push({ kind: "hull", nodeId: node.nodeId, points: ring, masked });
    }
  }

  // Edges in a stable order (sorted by edgeId). Only draw an edge whose endpoints
  // are both present in this slice (a cross-slice edge is drawn on its own tier).
  const edges = [...payload.edges].sort((a, b) => (a.edgeId < b.edgeId ? -1 : a.edgeId > b.edgeId ? 1 : 0));
  // RENDERER DEFENSE-IN-DEPTH (map-generation-v2 RC13 / round-1 review MAJOR-13):
  // a bidirectional passage is TWO edge records (A->B and B->A, the RC1 inverse)
  // for ONE physical corridor. Draw it ONCE to avoid SVG stroke-doubling, channel
  // over-paint, and door-glyph z-fighting. The dedup key is the ORDERED pair in the
  // edge's OWN orientation; an edge is skipped only when the OPPOSITE orientation of
  // the SAME node pair was already drawn (a true inverse). This deliberately does
  // NOT collapse two DISTINCT same-direction passages between the same room pair
  // (e.g. a curved + a straight a->b corridor), which are different edges. Stubs
  // (leadsToUndiscovered) and vertical links are self-contained and exempt. First
  // edge by the sorted order wins deterministically.
  const drawnOriented = new Set<string>();
  const orientKey = (a: string, b: string): string => `${a}\u0000${b}`;
  // Reverse-barrier lookup (review round-1 MINOR): when dedup drops the inverse
  // edge, a barrier that lived ONLY on that inverse must not vanish. Index each
  // full edge's barrier by its own orientation so the kept edge can fall back to
  // the inverse's barrier when it carries none of its own.
  const barrierByOrient = new Map<string, RenderEdge["barrier"]>();
  for (const e of edges) {
    if (e.barrier && !e.leadsToUndiscovered && e.to) barrierByOrient.set(orientKey(e.from, e.to), e.barrier);
  }
  for (const edge of edges) {
    const aCenter = centerOf.get(edge.from);
    const fromRingEarly = ringOf.get(edge.from);

    // VERTICAL edge (stairs/ladder/shaft): the far end is on ANOTHER Z-level, so
    // on this slice we never draw a corridor to a second node — we stamp a
    // self-contained stair LANDING docked to the room wall, tagged `vertical` so
    // the renderer paints treads + an up/down badge. Handled BEFORE the stub /
    // normal-corridor branches since a vertical link is neither.
    if (edge.direction === "up" || edge.direction === "down") {
      if (!aCenter || !fromRingEarly) continue;
      // Dock the landing to a wall. `up`/`down` have no real compass bearing, so
      // place the stamp on a deterministic wall per direction (up → the NE wall,
      // down → the SW wall) so a room with both does not overlap them.
      const dockDir: RenderDirection = edge.direction === "up" ? "northeast" : "southwest";
      const door = perimeterPointInDirection(fromRingEarly, aCenter, dockDir);
      const [dvx, dvy] = DIR_VEC[dockDir];
      const end: readonly [number, number] = [
        door[0] + dvx * VERTICAL_STAIR_LENGTH_FT,
        door[1] + dvy * VERTICAL_STAIR_LENGTH_FT,
      ];
      const landingSpine = [door, end] as const;
      commands.push({
        kind: "channel",
        edgeId: edge.edgeId,
        points: channelQuad(door, end, CHANNEL_HALF_WIDTH, 2),
        spine: landingSpine,
        halfWidth: CHANNEL_HALF_WIDTH,
        curved: false,
        destroyed: edge.destroyed === true,
        vertical: edge.direction,
      });
      if (edge.barrier) {
        commands.push({
          kind: "barrier",
          edgeId: edge.edgeId,
          at: door,
          barrierType: edge.barrier.barrierType,
          status: edge.barrier.status,
        });
      }
      continue;
    }

    // EGRESS edge (surface-egress addendum): the site's WAY OUT to the world. Drawn like a
    // stub (a short passage from the boundary room's doorway running OUT, no second room),
    // but tagged `egress` so the visual layer paints a dedicated EXIT affordance per kind
    // (stairs-up/trapdoor + "EXIT" badge, a door, a cave-mouth opening, a ramp, a portal ring)
    // instead of a plain "corridor continues" stub. MUST be checked BEFORE the
    // `leadsToUndiscovered` branch: a boundary egress edge to the exterior stub also carries
    // `leadsToUndiscovered`, so the stub branch would otherwise intercept it.
    // ONLY the EXTERIOR-STUB egress (no in-slice target, `to` empty) gets the off-wall EXIT
    // affordance. A DM-flagged surface exit to a REAL discovered anchor (a parent place the
    // party walked in from) keeps `to` set and FALLS THROUGH to normal corridor/vertical
    // drawing, so the connection to that real room is still drawn (review HIGH: an egress
    // tag must not sever a real discovered-anchor corridor into a stub).
    if (edge.egress && !edge.to) {
      if (!aCenter || !edge.direction || !fromRingEarly) continue;
      const door = perimeterPointInDirection(fromRingEarly, aCenter, edge.direction);
      const [dvx, dvy] = DIR_VEC[edge.direction];
      const end: readonly [number, number] = [door[0] + dvx * STUB_LENGTH_FT, door[1] + dvy * STUB_LENGTH_FT];
      const egressSpine = [door, end] as const;
      // Centerline thread (Layer 4) for parity with the stub branch, so the edge's
      // identity + data-edge-id persist for tests/overlays (review LOW).
      commands.push({
        kind: "edge",
        edgeId: edge.edgeId,
        points: egressSpine,
        curved: false,
        destroyed: edge.destroyed === true,
      });
      commands.push({
        kind: "channel",
        edgeId: edge.edgeId,
        points: channelQuad(door, end, CHANNEL_HALF_WIDTH, 2),
        spine: egressSpine,
        halfWidth: CHANNEL_HALF_WIDTH,
        curved: false,
        destroyed: edge.destroyed === true,
        egress: edge.egress,
      });
      if (edge.barrier) {
        commands.push({
          kind: "barrier",
          edgeId: edge.edgeId,
          at: door,
          barrierType: edge.barrier.barrierType,
          status: edge.barrier.status,
        });
      }
      continue;
    }

    // STUB edge (R4/R11.6): an opening to an UNDISCOVERED place. There is no
    // target node in the payload (`to` is empty), so we draw a short passage
    // running from the `from` room's doorway OUT into the rock in the edge's
    // direction, ending at an open, unexplored mouth. This makes "the corridor
    // continues" legible without any target geometry to reveal.
    if (edge.leadsToUndiscovered) {
      if (!aCenter || !edge.direction || !fromRingEarly) continue;
      const door = perimeterPointInDirection(fromRingEarly, aCenter, edge.direction);
      const [dvx, dvy] = DIR_VEC[edge.direction];
      const end: readonly [number, number] = [door[0] + dvx * STUB_LENGTH_FT, door[1] + dvy * STUB_LENGTH_FT];
      const stubSpine = [door, end] as const;
      commands.push({
        kind: "edge",
        edgeId: edge.edgeId,
        points: stubSpine,
        curved: false,
        destroyed: edge.destroyed === true,
      });
      commands.push({
        kind: "channel",
        edgeId: edge.edgeId,
        // Overlap only the ROOM end into the floor (overlapFt into `from`); the
        // far end stays flush with its unexplored mouth, no second room to merge.
        points: channelQuad(door, end, CHANNEL_HALF_WIDTH, 2),
        spine: stubSpine,
        halfWidth: CHANNEL_HALF_WIDTH,
        curved: false,
        destroyed: edge.destroyed === true,
        stub: true,
      });
      if (edge.barrier) {
        commands.push({
          kind: "barrier",
          edgeId: edge.edgeId,
          at: door,
          barrierType: edge.barrier.barrierType,
          status: edge.barrier.status,
        });
      }
      continue;
    }

    const bCenter = centerOf.get(edge.to);
    if (!aCenter || !bCenter) continue;

    // BIDIRECTIONAL DEDUP (RC13): skip only if the INVERSE orientation (to->from)
    // of this exact node pair was already drawn — a true reverse edge. Two distinct
    // same-direction passages are NOT collapsed. Record this orientation as drawn.
    if (drawnOriented.has(orientKey(edge.to, edge.from))) continue;
    drawnOriented.add(orientKey(edge.from, edge.to));

    // DOORWAY placement (R8/R9): leave `from` through the wall in the edge's
    // compass direction, and enter `to` through the opposite wall — so a corridor
    // visibly exits the correct side of each room. When the edge carries no
    // direction (legacy payloads), fall back to the room centers (prior behavior).
    const fromRing = ringOf.get(edge.from);
    const toRing = ringOf.get(edge.to);
    const doorFrom =
      edge.direction && fromRing
        ? perimeterPointInDirection(fromRing, aCenter, edge.direction)
        : aCenter;
    const doorTo =
      edge.direction && toRing
        ? perimeterPointInDirection(toRing, bCenter, INVERSE_DIR[edge.direction])
        : bCenter;

    const curved = isCurved(edge);
    const spine = curved
      ? [doorFrom, midpointArc(doorFrom, doorTo, edge), doorTo]
      : [doorFrom, doorTo];
    commands.push({
      kind: "edge",
      edgeId: edge.edgeId,
      points: spine,
      curved,
      destroyed: edge.destroyed === true,
    });
    // The carved FLOOR CHANNEL for this corridor — the strip of floor the
    // renderer punches through the rock so the doorway reads as an opening
    // (R8/R11). Overlap each end 2ft into its room so the floors merge at the
    // mouth with no seam. Only when we have real doorway endpoints (direction
    // present); a legacy center-to-center edge gets no channel (it has no wall
    // to carve through meaningfully).
    if (edge.direction && fromRing && toRing) {
      commands.push({
        kind: "channel",
        edgeId: edge.edgeId,
        points: channelQuad(doorFrom, doorTo, CHANNEL_HALF_WIDTH, 2),
        spine,
        halfWidth: CHANNEL_HALF_WIDTH,
        curved,
        destroyed: edge.destroyed === true,
      });
    }
    // Barrier glyph: prefer this edge's own barrier; if it has none, fall back to
    // the inverse edge's barrier (which dedup dropped) so a one-sided barrier is
    // not lost (review round-1 MINOR).
    const barrier = edge.barrier ?? barrierByOrient.get(orientKey(edge.to, edge.from));
    if (barrier) {
      // The door/gate glyph sits ON the `from` wall doorway (where the opening
      // actually is), not floating at the geometric midpoint between centers.
      commands.push({
        kind: "barrier",
        edgeId: edge.edgeId,
        at: doorFrom,
        barrierType: barrier.barrierType,
        status: barrier.status,
      });
    }
  }

  return commands;
}

/**
 * The control point for a curved corridor's Bezier: the edge midpoint pushed
 * perpendicular by a deflection-dependent amount (right curves bow one way, left
 * the other). Deterministic — no RNG; the curve direction follows the fixed
 * bearing deflection, so the same edge always bows the same way.
 */
function midpointArc(
  a: readonly [number, number],
  b: readonly [number, number],
  edge: RenderEdge,
): readonly [number, number] {
  const mx = (a[0] + b[0]) / 2;
  const my = (a[1] + b[1]) / 2;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1;
  // Perpendicular unit vector.
  const px = -dy / len;
  const py = dx / len;
  const bow = deflectionBow(edge.bearingDeflection);
  return [mx + px * bow, my + py * bow];
}

/** How far (and which way) a deflection bows the corridor midpoint, in feet. */
function deflectionBow(d: RenderEdge["bearingDeflection"]): number {
  switch (d) {
    case "gentle_curve_right":
      return 4;
    case "gentle_curve_left":
      return -4;
    case "sharp_right":
      return 9;
    case "sharp_left":
      return -9;
    case "meandering":
      return 6;
    default:
      return 0;
  }
}
